import { RootAction, RootActionType } from '../actions';
import { RootState } from '../reducer';

export type FocusedCellState =
  | InactiveFocusedCellState
  | ActiveFocusedCellState;

type InactiveFocusedCellState = {
  column: null;
  columnCount: number;
  hasDOMFocus: boolean;
  row: null;
};

type ActiveFocusedCellState = {
  column: number;
  columnCount: number;
  hasDOMFocus: boolean;
  row: number;
};

export const initialFocusedCellState = {
  column: null,
  columnCount: 0,
  hasDOMFocus: false,
  row: null,
};

export function focusedCellReducer(
  state: FocusedCellState,
  action: RootAction,
  rootState: RootState,
): FocusedCellState {
  switch (action.type) {
    case RootActionType.BlurLinesheetInDOM:
      if (!state.hasDOMFocus) return state;

      return {
        ...state,
        hasDOMFocus: false,
      };

    case RootActionType.FocusLinesheetInDOM:
      if (state.hasDOMFocus) return state;

      return {
        ...state,
        hasDOMFocus: true,
      };

    case RootActionType.MoveLinesheetFocusedCellBackward:
      if (state.column === null) return state;
      if (state.column === 0 && state.row === 0) return state;

      return {
        ...state,
        column: state.column === 0 ? state.columnCount - 1 : state.column - 1,
        row: state.column === 0 ? state.row - 1 : state.row,
      };

    case RootActionType.MoveLinesheetFocusedCellDown:
      if (state.row === null) return state;
      if (state.row + 1 === rootState.linesheet.rows?.length) {
        return state;
      }

      return {
        ...state,
        row: state.row + 1,
      };

    case RootActionType.MoveLinesheetFocusedCellForward:
      if (state.column === null) {
        return {
          ...state,
          column: 0,
          row: 0,
        };
      }

      if (
        state.column + 1 === state.columnCount &&
        state.row + 1 === rootState.linesheet.rows?.length
      ) {
        return state;
      }

      return {
        ...state,
        row: state.column + 1 === state.columnCount ? state.row + 1 : state.row,
        column: state.column + 1 === state.columnCount ? 0 : state.column + 1,
      };

    case RootActionType.MoveLinesheetFocusedCellLeft:
      if (!state.column) return state;

      return {
        ...state,
        column: state.column - 1,
      };

    case RootActionType.MoveLinesheetFocusedCellRight:
      if (state.column === null || state.column + 1 === state.columnCount) {
        return state;
      }

      return {
        ...state,
        column: state.column + 1,
      };

    case RootActionType.MoveLinesheetFocusedCellUp:
      if (state.column === null || state.row === 0) {
        return state;
      }

      return {
        ...state,
        row: state.row - 1,
      };

    case RootActionType.ReceiveLinesheetColumnCount:
      if (state.columnCount === action.payload) return state;

      return {
        ...state,
        columnCount: action.payload,
      };

    case RootActionType.ResetLinesheetFocus:
      if (state.column === null) return state;

      return {
        ...state,
        column: initialFocusedCellState.column,
        row: initialFocusedCellState.row,
      };

    case RootActionType.SetLinesheetFocusedCell:
      if (
        state.column === action.payload.column &&
        state.row === action.payload.row
      ) {
        return state;
      }

      return {
        ...state,
        column: action.payload.column,
        row: action.payload.row,
      };

    default:
      return state;
  }
}
